import React, { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import type { AxiosError } from 'axios'

import { triggerToast } from '@/components/Toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogPortal,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { useCreateCompany, useUpdateCompany } from '@/features/company/hooks'
import { useCompanyStore } from '@/features/company/store'
import { CompanyStatus } from '@/schema'
import { MAX_TEXT_LENGTH } from '@/utils/constants'

interface CompanyFormData {
  name: string
  status: CompanyStatus
}

const CompanyDialog: React.FC = () => {
  const { t } = useTranslation()
  const {
    editingCompany,
    dialogOpen: open,
    clearEditingCompany,
    setDialogOpen,
  } = useCompanyStore()

  const isEditMode = useMemo(() => !!editingCompany, [editingCompany])

  const {
    handleSubmit,
    reset,
    register,
    formState: { errors, isValid },
    setValue,
    setError,
    control,
  } = useForm<CompanyFormData>({
    mode: 'onChange',
    defaultValues: {
      name: '',
      status: CompanyStatus.ACTIVE,
    },
  })

  const createMutation = useCreateCompany()
  const updateMutation = useUpdateCompany()

  const handleApiError = (error: unknown) => {
    const axiosError = error as AxiosError<{ error?: { message?: string } }>
    const errorMessage =
      axiosError.response?.data?.error?.message ||
      t('company.dialog.messages.errorGeneric')

    setError('name', {
      type: 'manual',
      message: errorMessage,
    })
  }

  useEffect(() => {
    if (isEditMode && editingCompany) {
      setValue('name', editingCompany.name)
      setValue('status', editingCompany.status)
    }
  }, [isEditMode, editingCompany, setValue])

  useEffect(() => {
    if (!open) {
      reset({
        name: '',
        status: CompanyStatus.ACTIVE,
      })
      createMutation.reset()
      updateMutation.reset()
      if (isEditMode) {
        clearEditingCompany()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset])

  const onSubmit = (data: CompanyFormData) => {
    const formData = {
      name: data.name.trim(),
      status: data.status,
    }

    if (isEditMode && editingCompany) {
      updateMutation.mutate(
        {
          id: editingCompany.id,
          data: formData,
        },
        {
          onSuccess: () => {
            triggerToast('success', t('company.dialog.messages.updateSuccess'))
            clearEditingCompany()
            setDialogOpen(false)
          },
          onError: handleApiError,
        }
      )
    } else {
      createMutation.mutate(formData, {
        onSuccess: () => {
          triggerToast('success', t('company.dialog.messages.addSuccess'))
          setDialogOpen(false)
        },
        onError: handleApiError,
      })
    }
  }

  const isSaving = useMemo(
    () => createMutation.isPending || updateMutation.isPending,
    [createMutation.isPending, updateMutation.isPending]
  )

  const handleOpenChange = (newOpen: boolean) => {
    setDialogOpen(newOpen)
    if (!newOpen && isEditMode) {
      clearEditingCompany()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogPortal>
        <DialogContent className="sm:max-w-[480px] bg-white shadow-xl rounded-2xl p-6">
          <DialogHeader className="space-y-0.5">
            <DialogTitle className="m-0 text-md font-semibold text-primary-900">
              {isEditMode
                ? t('company.dialog.editTitle')
                : t('company.dialog.addTitle')}
            </DialogTitle>
          </DialogHeader>

          <form
            id="company-form"
            onSubmit={handleSubmit(onSubmit)}
            className="space-y-4"
          >
            <div className="space-y-1">
              <label htmlFor="name" className="text-sm font-medium">
                {t('company.dialog.fields.name')}
              </label>
              <Input
                id="name"
                className="px-lg py-md rounded-md border-solid gap-md border-primary bg-white text-md"
                placeholder={t('company.dialog.placeholders.enterName')}
                disabled={isSaving}
                {...register('name', {
                  required: t('company.dialog.validation.nameRequired'),
                  validate: (value) => {
                    if (!value.trim()) {
                      return t('company.dialog.validation.nameRequired')
                    }
                    return true
                  },
                })}
                maxLength={MAX_TEXT_LENGTH}
              />
              {errors.name && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.name.message}
                </p>
              )}
            </div>

            <div className="space-y-1">
              <label htmlFor="status" className="text-sm font-medium">
                {t('company.dialog.fields.status')}
              </label>
              <Controller
                name="status"
                control={control}
                rules={{
                  required: t('company.dialog.validation.statusRequired'),
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    fullWidth
                    options={[
                      {
                        value: CompanyStatus.ACTIVE,
                        label: t('company.status.active'),
                      },
                      {
                        value: CompanyStatus.ARCHIVED,
                        label: t('company.status.archived'),
                      },
                    ]}
                    disabled={isSaving}
                    placeholder={t('company.dialog.placeholders.selectStatus')}
                  />
                )}
              />
              {errors.status && (
                <p className="text-sm text-red-500 mt-1">
                  {errors.status.message}
                </p>
              )}
            </div>
          </form>

          <DialogFooter className="flex justify-stretch !space-x-0 gap-lg pt-3">
            <Button
              type="button"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-secondary-700 hover:text-secondary-hover text-md border-solid border-primary bg-white hover:bg-primary-hover"
              variant="default"
              onClick={() => handleOpenChange(false)}
              disabled={isSaving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="company-form"
              className="flex-1 rounded-md py-2.5 px-xl font-semibold text-md border-solid border-transparent !text-white bg-brand-solid hover:bg-brand-solid-hover disabled:bg-gray-light-100 disabled:!text-gray-light-400 disabled:hover:bg-gray-light-200"
              variant="default"
              disabled={isSaving || !isValid}
            >
              {isSaving ? t('common.saving') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default CompanyDialog
